import "./ArtworkControls.css"
import { useRef, useState } from "react"
import { useI18n } from "../i18n/useI18n"
import type { CardArtwork } from "../schemas"

type Props = {
  addLabel: string
  helpLabel?: string
  artwork?: CardArtwork
  busy: boolean
  className?: string
  emptyLabel?: string
  error?: string | null
  onRemove?: () => void
  onSelectFile?: (file: File | null) => void
  onRetrySync?: () => void
  readyLabel: string
  replaceLabel: string
  retrySyncBusy?: boolean
  syncStatusLabel?: string | null
}

type OverlayControlsProps = {
  error?: string | null
  onRemove?: () => void
  onRetrySync?: () => void
  removeLabel: string
  retrySyncBusy?: boolean
  retrySyncLabel: string
  retryingLabel: string
  syncStatusLabel?: string | null
}

type DropTargetProps = {
  artwork?: CardArtwork
  addLabel: string
  replaceLabel: string
  busy: boolean
  onSelectFile: (file: File | null) => void
}

function useArtworkInput(
  onSelectFile: (file: File | null) => void,
  busy: boolean,
) {
  const inputRef = useRef<HTMLInputElement>(null)
  const dragDepthRef = useRef(0)
  const [isDragActive, setIsDragActive] = useState(false)

  const openFilePicker = () => {
    if (busy) return
    inputRef.current?.click()
  }

  const resetDragState = () => {
    dragDepthRef.current = 0
    setIsDragActive(false)
  }

  const handleDroppedFile = (file: File | null) => {
    resetDragState()
    if (busy) return
    onSelectFile(file)
  }

  return {
    inputRef,
    isDragActive,
    openFilePicker,
    resetDragState,
    handleDroppedFile,
    onDragEnter(event: React.DragEvent) {
      event.preventDefault()
      if (busy) return
      dragDepthRef.current += 1
      setIsDragActive(true)
    },
    onDragLeave(event: React.DragEvent) {
      event.preventDefault()
      if (busy) return
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
      if (dragDepthRef.current === 0) {
        setIsDragActive(false)
      }
    },
    onDragOver(event: React.DragEvent) {
      event.preventDefault()
      if (busy) return
      event.dataTransfer.dropEffect = "copy"
    },
  }
}

export function ArtworkDropTarget({
  artwork,
  addLabel,
  replaceLabel,
  busy,
  onSelectFile,
}: DropTargetProps) {
  const { t } = useI18n()
  const {
    handleDroppedFile,
    inputRef,
    isDragActive,
    onDragEnter,
    onDragLeave,
    onDragOver,
    openFilePicker,
  } = useArtworkInput(onSelectFile, busy)

  const title = busy
    ? t("ui.artwork.dropTitleBusy")
    : artwork
      ? t("ui.artwork.dropTitleReplace")
      : t("ui.artwork.dropTitleEmpty")
  const body = busy
    ? t("ui.artwork.dropBodyBusy")
    : artwork
      ? replaceLabel
      : addLabel
  const isFilled = Boolean(artwork)

  return (
    <div
      className={`artwork-drop-target-shell${isFilled ? " artwork-drop-target-shell-filled" : ""}`}
    >
      <input
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif"
        className="artwork-controls-input"
        onChange={(event) =>
          onSelectFile(event.currentTarget.files?.[0] ?? null)
        }
        ref={inputRef}
        type="file"
      />
      <button
        className={`artwork-drop-target${isFilled ? " artwork-drop-target-filled" : " artwork-drop-target-empty"}${isDragActive ? " artwork-drop-target-active" : ""}${busy ? " artwork-drop-target-busy" : ""}`}
        disabled={busy}
        onClick={openFilePicker}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={(event) => {
          event.preventDefault()
          const file = event.dataTransfer.files?.[0] ?? null
          handleDroppedFile(file)
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            openFilePicker()
          }
        }}
        type="button"
      >
        <strong className="artwork-drop-target-title">{title}</strong>
        <span className="artwork-drop-target-body">{body}</span>
      </button>
    </div>
  )
}

export function ArtworkControls({
  addLabel: _addLabel,
  artwork,
  busy,
  className,
  emptyLabel,
  error,
  helpLabel,
  onRemove,
  onSelectFile,
  onRetrySync,
  readyLabel,
  replaceLabel,
  retrySyncBusy = false,
  syncStatusLabel,
}: Props) {
  const { t } = useI18n()
  const artworkVariant = artwork?.variants.find(
    (variant) => variant.kind === "card_art",
  )
  const statusLabel = busy
    ? t("ui.artwork.preparing")
    : artwork
      ? `${readyLabel} · ${artworkVariant?.width ?? "?"}×${artworkVariant?.height ?? "?"}`
      : emptyLabel

  return (
    <div className={["artwork-controls", className].filter(Boolean).join(" ")}>
      {onSelectFile && (
        <ArtworkDropTarget
          addLabel={_addLabel}
          artwork={artwork}
          busy={busy}
          onSelectFile={onSelectFile}
          replaceLabel={replaceLabel}
        />
      )}
      {artwork && !busy && onRemove && (
        <button
          className="card-editor-action-button card-editor-action-button-secondary"
          onClick={onRemove}
          type="button"
        >
          {t("ui.artwork.removeImage")}
        </button>
      )}
      {statusLabel && <p className="artwork-controls-status">{statusLabel}</p>}
      <p className="artwork-controls-help">
        {helpLabel ?? t("ui.artwork.help")}
      </p>
      {(syncStatusLabel || onRetrySync) && (
        <div className="artwork-controls-sync">
          {syncStatusLabel && (
            <p className="artwork-controls-sync-status">{syncStatusLabel}</p>
          )}
          {onRetrySync && (
            <button
              className="artwork-controls-sync-retry"
              disabled={retrySyncBusy || busy}
              onClick={onRetrySync}
              type="button"
            >
              {retrySyncBusy
                ? t("ui.artwork.retrying")
                : t("ui.artwork.retrySync")}
            </button>
          )}
        </div>
      )}
      {error && <p className="artwork-controls-error field-error">{error}</p>}
    </div>
  )
}

export function ArtworkOverlayControls({
  error,
  onRemove,
  onRetrySync,
  removeLabel,
  retrySyncBusy = false,
  retrySyncLabel,
  retryingLabel,
  syncStatusLabel,
}: OverlayControlsProps) {
  if (!error && !onRemove && !onRetrySync && !syncStatusLabel) {
    return null
  }

  return (
    <div className="artwork-overlay-controls">
      {syncStatusLabel && (
        <span className="artwork-overlay-pill artwork-overlay-pill-status">
          {syncStatusLabel}
        </span>
      )}
      {error && (
        <span className="artwork-overlay-pill artwork-overlay-pill-error">
          {error}
        </span>
      )}
      {onRetrySync && (
        <button
          className="artwork-overlay-pill artwork-overlay-pill-action"
          disabled={retrySyncBusy}
          onClick={onRetrySync}
          type="button"
        >
          {retrySyncBusy ? retryingLabel : retrySyncLabel}
        </button>
      )}
      {onRemove && (
        <button
          className="artwork-overlay-pill artwork-overlay-pill-action"
          onClick={onRemove}
          type="button"
        >
          {removeLabel}
        </button>
      )}
    </div>
  )
}
